import { insm } from '@atlaskit/insm';
import traceUFOPageload from '@atlaskit/react-ufo/trace-pageload';
import traceUFOTransition from '@atlaskit/react-ufo/trace-transition';

import { browserMetrics } from '@atlassian/browser-metrics';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { getMark } from '@confluence/performance/entry-points/performance.ts';
import { pauseLowPriorityEvents } from '@confluence/analytics-event-delay';

import { browserMetricsState } from './utils/index.ts';
import { getPerformanceEntries } from './collectFromPerfMarkers.ts';
import { addAllBM3TimingsToUFO } from './addAllBM3TimingsToUFO.ts';
import { collectInitialLoadWaterfallTimings } from './collectInitialLoadWaterfallTimings.ts';

/**
 * Function that begins a browser-metrics page loading timer for the current navigation.
 * Intended to be plugged into the RouteManager onRoute() callback.
 * If a page loading timer has already been started but is unfinished, this function cancels and replaces it.
 *
 * @param routeName The matched route name
 * @param transitionId The current transition ID (0 means initial)
 */
export const startBrowserMetricsPageLoad = (
	routeName = '',
	ufoName = routeName,
	transitionId: number,
	contentId = '',
): void => {
	// Begin the loading timer _immediately_ using BM libraries generic meta startPageLoad metric.
	// Routes should later call stopPageLoad on the actual PageLoadMetric that is specifically defined for
	// the route in order to claim this page load.
	browserMetrics.startPageLoad({ isInitial: transitionId === 0 });

	if (transitionId === 0) {
		traceUFOPageload(ufoName.toLocaleLowerCase());
		if (expValEquals('cc_editor_interactivity_monitoring', 'isEnabled', true)) {
			insm.start(ufoName.toLocaleLowerCase(), {
				initial: true,
				contentId,
			});
			insm.startHeavyTask('PageLoad');
		}
	} else {
		traceUFOTransition(ufoName.toLocaleLowerCase());
		if (expValEquals('cc_editor_interactivity_monitoring', 'isEnabled', true)) {
			insm.start(ufoName.toLocaleLowerCase(), {
				initial: false,
				contentId,
			});
			insm.startHeavyTask('PageLoad');
		}
	}

	performance.mark('browser-metric.start');

	const pageLoadMetric = browserMetrics.getPageLoadMetric();

	browserMetricsState.transitionId = transitionId;
	browserMetricsState.pageLoadStartTimestamp = pageLoadMetric.getData().start ?? performance.now();

	browserMetricsState.previousRoute = browserMetricsState.routeName;
	browserMetricsState.routeName = routeName;
	browserMetrics.setRoute(routeName);

	browserMetricsState.previousContentId = browserMetricsState.contentId;
	browserMetricsState.contentId = contentId;

	// will pause low priority events to avoid impacting page load performance
	// once the pageLoadEnd is triggered the resumeLowPriorityEvents will be called
	void pauseLowPriorityEvents(routeName);

	if (pageLoadMetric.isInitialLoad() && window?.__SSR_RENDERED__) {
		// Mark the legacy FMP point if server-side rendered
		pageLoadMetric.mark('fy21-legacy-fmp', getMark('CFP-63.ssr-ttr'));
	}
	pageLoadMetric.onStop(
		() => {
			// When BM3 is being deprecated, all measurements are added to UFO via the interop layer,
			// so there's nothing left to do here.
			if (fg('confluence_fe_deprecate_bm3')) {
				return;
			}

			if (pageLoadMetric.isInitialLoad()) {
				collectInitialLoadWaterfallTimings();
			}

			// Copy all /wf markers to the page load metric
			// - window.performance.mark('wf/something.start/.end')
			// - WaterfallMetrics.run()
			// - WaterfallMetrics.markMeasureStart/markMeasureEnd()
			const currentMetric = browserMetrics.getPageLoadMetric();
			getPerformanceEntries('wf/').forEach(({ name, startTime }) => {
				currentMetric.mark(name, startTime);
			});

			// NOTE: currentMetric is stopping _too early_ and some segment-end marks are added _after_ the metric is already stopped
			// To see this, console.log the following and compare the outputs:
			// console.log({ ...currentMetric.getData().marks }); // the marks available at the time of the object spread
			// console.log(currentMetric.getData().marks); // the marks available when browser can log to the console
			// what is logged at the bottom console.log is the contents of the memory reference of (currentMetric.getData().marks, which seems to be getting modified at a later point in time.
			addAllBM3TimingsToUFO(currentMetric.getData().marks);
		},
		() => {},
	);
};
